import {Injectable} from '@angular/core';
import {UntypedFormControl, FormGroup, Validators} from '@angular/forms';
import {CustomValidatorsService} from './custom-validators.service';
import {InstrumentItem} from '../models/InstrumentItem';
import moment from 'moment';

let CURRENCIES = [
    'CNY', 'CNH', 'GBP', 'TWD', 'AUD', 'USD', 'HKD', 'JPY', 'KRW', 'SGD', 'THB', 'PHP', 'MYR', 'EUR', 'NZD'
];

CURRENCIES.sort();

@Injectable({
  providedIn: 'root'
})
export class ConstantsService {
    private instrumentFieldsOnChangeEvents = {
        onIrsCmsLeg1LegTypeChange: (leg1LegType: string, leg2LegTypeFormControl: UntypedFormControl) => {
            if (leg1LegType === '0') {
                leg2LegTypeFormControl.setValue('1', { emitEvent: false });
                leg2LegTypeFormControl.markAsPristine();
                leg2LegTypeFormControl.disable({ emitEvent: false });
            } else if (leg1LegType === '1') {
                leg2LegTypeFormControl.setValue('0', { emitEvent: false });
                leg2LegTypeFormControl.markAsPristine();
                leg2LegTypeFormControl.disable({ emitEvent: false });
            } else {
                if (leg2LegTypeFormControl.value === '0' || leg2LegTypeFormControl.value === '1') {
                    leg2LegTypeFormControl.setValue('', { emitEvent: false });
                }
                leg2LegTypeFormControl.markAsPristine();
                leg2LegTypeFormControl.enable({ emitEvent: false });
            }
        }, onIrsCmsLeg2LegTypeChange: (leg2LegType: string, leg1LegTypeFormControl: UntypedFormControl) => {
            if (leg2LegType === '0') {
                leg1LegTypeFormControl.setValue('1', { emitEvent: false });
                leg1LegTypeFormControl.markAsPristine();
                leg1LegTypeFormControl.disable({ emitEvent: false });
            } else if (leg2LegType === '1') {
                leg1LegTypeFormControl.setValue('0', { emitEvent: false });
                leg1LegTypeFormControl.markAsPristine();
                leg1LegTypeFormControl.disable({ emitEvent: false });
            } else {
                if (leg1LegTypeFormControl.value === '0' || leg1LegTypeFormControl.value === '1') {
                    leg1LegTypeFormControl.setValue('', { emitEvent: false });
                }
                leg1LegTypeFormControl.markAsPristine();
                leg1LegTypeFormControl.enable({ emitEvent: false });
            }
        }
    };

    instrumentColumnConfig = {
        fund: { columnName: 'Fund', class: 'px-w-115' },
        side: { columnName: 'Side', class: 'px-w-90' },
        maturityDate: { columnName: 'Maturity', class: 'px-w-80', dateField: true },
        bondMaturityDate: { columnName: 'Bond Maturity', class: 'px-w-90', dateField: true },
        notional: { columnName: 'Amount', class: 'px-w-65' },
        payNotional: { columnName: 'Pay Notional', class: 'px-w-85' },
        irsNotional: { columnName: 'Amount', class: 'px-w-65' },
        bondNotional: { columnName: 'Bond Notional', class: 'px-w-85' },
        fxRate: { columnName: 'FX Rate', class: 'px-w-65' },
        repoRate: { columnName: 'Repo Rate', class: 'px-w-85' },

        fixedRateCoupon: {
            columnName: 'Coupon', class: 'px-w-75',
            numberBetween: {
                min: -100,
                max: 100
            }
        },
        id: { columnName: 'ID', class: 'px-w-65' },
        counterParty: { columnName: 'Counter Party', class: 'px-w-85' },
        legType: {
            columnName: 'Leg Type', class: 'px-w-75',
            selectOptions: [
                { key: 'Fixed', value: '0' },
                { key: 'Float', value: '1' },
                { key: 'Cash', value: '2' },
                { key: 'Option', value: '9' }
            ]
        },
        irsLegType: {
            columnName: 'Leg Type', class: 'px-w-75',
            selectOptions: [
                { key: 'Fixed', value: '0' },
                { key: 'Float', value: '1' }
            ]
        },
        currency: {
            columnName: 'Currency', class: 'px-w-65',
            selectOptions: CURRENCIES
        },
        bondCurrency: {
            columnName: 'Bond Currency', class: 'px-w-85',
            selectOptions: CURRENCIES
        },
        againstCurrency: {
            columnName: 'Against Currency', class: 'px-w-115',
            selectOptions: CURRENCIES
        },
        pnlAccounting: { columnName: 'Actual value', class: 'px-w-75', onTradeExecuted: true },
        haircutAfterPricing: {
            columnName: 'Haircut After Pricing', class: 'px-w-115',
            numberBetween: {
                min: 0,
                max: 1
            }
        },
        priceAccounting: { columnName: 'P&L Actual value', class: 'px-w-95', onTradeExecuted: true },
        couponFrequency: {
            columnName: 'Frequency', class: 'px-w-90',
            selectOptions: [
                { key: 'Zero-coupon', value: '0' },
                { key: 'Monthly', value: '12' },
                { key: 'Quarterly', value: '4' },
                { key: 'Semi-annual', value: '2' },
                { key: 'Annual', value: '1' },
            ]
        },
        spread: { columnName: 'Spread', class: 'px-w-65' },
        refForward: {
            columnName: 'Ref Forword', class: 'px-w-75',
            selectOptions: [
                { key: '1M', value: '1' },
                { key: '3M', value: '3' },
                { key: '6M', value: '6' },
                { key: '1Y', value: '120' }
            ]
        },
        npvUsd: { columnName: 'NPV(USD)', class: 'px-w-75' },
        bondPrice: { columnName: 'Bond Price', class: 'px-w-85' },
        disCurve: {
            columnName: 'DiscCurve', class: 'px-w-90',
            selectOptions: [
                'GOVT',
                'SWAP'
            ]
        },
        issueDate: { columnName: 'Issue Date', class: 'px-w-80', dateField: true },
        bondIssueDate: { columnName: 'Bond Issue Date', class: 'px-w-95', dateField: true },
        principalExchanged: {
            columnName: 'Principal exchanged', class: 'px-w-115',
            selectOptions: [
                'M',
                'Y',
                'A',
                'N',
                'E'
            ]
        }
    };

    errorMessages = {
        required: 'Value is required.',
        onlyNumber: 'Only numbers are accepted.',
        lessThan100: 'Value must be less than or equal to 100.',
        numberBetween0And1: 'Value must be between 0 and 1.',
        numberBetweenMinus100And100: 'Value must be between -100 and 100.',
        invalidDate: 'Invalid date value. Valid example. 20/12/2018.',
        futureDate: 'Date should be a future date.',
        irsWorkaround: 'For paid leg enter the Notional as a negative value, For received leg enter the Notional as a positive value.'
    };

    instrumentFieldValidations: any = {
        leg1Id: {
            validators: [Validators.required],
            validationMessages: [this.errorMessages.required]
        },
        leg1Currency: {
            validators: [Validators.required],
            validationMessages: [this.errorMessages.required]
        },
        leg1BondCurrency: {
            validators: [Validators.required],
            validationMessages: [this.errorMessages.required]
        },
        leg1AgainstCurrency: {
            validators: [Validators.required],
            validationMessages: [this.errorMessages.required]
        },
        leg1PnlAccounting: {
            validators: [Validators.required, Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.required, this.errorMessages.onlyNumber]
        },
        leg1HaircutAfterPricing: {
            validators: [this.customValidatorsService.numberBetweenValidator(0, 1)],
            validationMessages: [this.errorMessages.numberBetween0And1]
        },
        leg1Notional: {
            validators: [Validators.required, Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.required, this.errorMessages.onlyNumber]
        },
        leg1PayNotional: {
            validators: [Validators.required, Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.required, this.errorMessages.onlyNumber]
        },
        leg1IrsNotional: {
            validators: [this.customValidatorsService.irsNotionalValidator(), Validators.required, Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.irsWorkaround, this.errorMessages.required, this.errorMessages.onlyNumber]
        },
        leg1BondNotional: {
            validators: [Validators.required, Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.required, this.errorMessages.onlyNumber]
        },

        leg1FxRate: {
            validators: [Validators.required, Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.required, this.errorMessages.onlyNumber]
        },
        leg1RepoRate: {
            validators: [Validators.required, Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.required, this.errorMessages.onlyNumber]
        },
        leg1PriceAccounting: {
            validators: [Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.onlyNumber]
        },
        leg1FixedRateCoupon: {
            validators: [Validators.required, Validators.pattern(/^[+-]?\d*\.?\d*$/),
            this.customValidatorsService.numberBetweenValidator(-100, 100)],
            validationMessages: [
                this.errorMessages.required,
                this.errorMessages.onlyNumber,
                this.errorMessages.numberBetweenMinus100And100
            ]
        },
        leg1CouponFrequency: {
            validators: [Validators.required],
            validationMessages: [this.errorMessages.required]
        },
        leg1Spread: {
            validators: [Validators.required, Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.required, this.errorMessages.onlyNumber]
        },
        leg1RefForward: {
            validators: [Validators.required],
            validationMessages: [this.errorMessages.required]
        },
        leg1LegType: {
            validators: [Validators.required],
            validationMessages: [this.errorMessages.required]
        },
        leg1MaturityDate: {
            validators: [Validators.required, this.customValidatorsService.dateFormatValidator('D/M/YYYY'),
            this.customValidatorsService.futureDate('D/M/YYYY')],
            validationMessages: [
                this.errorMessages.required,
                this.errorMessages.invalidDate,
                this.errorMessages.futureDate
            ]
        },
        leg1BondMaturityDate: {
            validators: [Validators.required, this.customValidatorsService.dateFormatValidator('D/M/YYYY'),
            this.customValidatorsService.futureDate('D/M/YYYY')],
            validationMessages: [
                this.errorMessages.required,
                this.errorMessages.invalidDate,
                this.errorMessages.futureDate
            ]
        },
        leg1NpvUsd: {
            validators: [Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.onlyNumber]
        },
        leg1BondPrice: {
            validators: [Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.onlyNumber]
        },
        leg1DisCurve: {
            validators: [Validators.required],
            validationMessages: [this.errorMessages.required]
        },
        leg1IssueDate: {
            validators: [this.customValidatorsService.dateFormatValidator('D/M/YYYY')],
            validationMessages: [this.errorMessages.invalidDate]
        },
        leg1BondIssueDate: {
            validators: [this.customValidatorsService.dateFormatValidator('D/M/YYYY')],
            validationMessages: [this.errorMessages.invalidDate]
        },
        leg1PrincipalExchanged: {
            validators: [Validators.required],
            validationMessages: [this.errorMessages.required]
        },
        leg2Id: {
            validators: [Validators.required],
            validationMessages: [this.errorMessages.required]
        },
        leg2Currency: {
            validators: [Validators.required],
            validationMessages: [this.errorMessages.required]
        },
        leg2BondCurrency: {
            validators: [Validators.required],
            validationMessages: [this.errorMessages.required]
        },
        leg2AgainstCurrency: {
            validators: [Validators.required],
            validationMessages: [this.errorMessages.required]
        },
        leg2PnlAccounting: {
            validators: [Validators.required, Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.required, this.errorMessages.onlyNumber]
        },
        leg2HaircutAfterPricing: {
            validators: [this.customValidatorsService.numberBetweenValidator(0, 1)],
            validationMessages: [this.errorMessages.numberBetween0And1]
        },
        leg2Notional: {
            validators: [Validators.required, Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.required, this.errorMessages.onlyNumber]
        },
        leg2PayNotional: {
            validators: [Validators.required, Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.required, this.errorMessages.onlyNumber]
        },
        leg2IrsNotional: {
            validators: [this.customValidatorsService.irsNotionalValidator(), Validators.required, Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.irsWorkaround, this.errorMessages.required, this.errorMessages.onlyNumber]
        },
        leg2BondNotional: {
            validators: [Validators.required, Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.required, this.errorMessages.onlyNumber]
        },
        leg2FxRate: {
            validators: [Validators.required, Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.required, this.errorMessages.onlyNumber]
        },
        leg2RepoRate: {
            validators: [Validators.required, Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.required, this.errorMessages.onlyNumber]
        },
        leg2PriceAccounting: {
            validators: [Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.onlyNumber]
        },
        leg2FixedRateCoupon: {
            validators: [Validators.required, Validators.pattern(/^[+-]?\d*\.?\d*$/),
            this.customValidatorsService.numberBetweenValidator(-100, 100)],
            validationMessages: [
                this.errorMessages.required,
                this.errorMessages.onlyNumber,
                this.errorMessages.numberBetweenMinus100And100
            ]
        },
        leg2CouponFrequency: {
            validators: [Validators.required],
            validationMessages: [this.errorMessages.required]
        },

        leg2Spread: {
            validators: [Validators.required, Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.required, this.errorMessages.onlyNumber]
        },
        leg2RefForward: {
            validators: [Validators.required],
            validationMessages: [this.errorMessages.required]
        },

        leg2LegType: {
            validators: [Validators.required],
            validationMessages: [this.errorMessages.required]
        },
        leg2MaturityDate: {
            validators: [Validators.required, this.customValidatorsService.dateFormatValidator('D/M/YYYY'),
            this.customValidatorsService.futureDate('D/M/YYYY')],
            validationMessages: [
                this.errorMessages.required,
                this.errorMessages.invalidDate,
                this.errorMessages.futureDate
            ]
        },
        leg2BondMaturityDate: {
            validators: [Validators.required, this.customValidatorsService.dateFormatValidator('D/M/YYYY'),
            this.customValidatorsService.futureDate('D/M/YYYY')],
            validationMessages: [
                this.errorMessages.required,
                this.errorMessages.invalidDate,
                this.errorMessages.futureDate
            ]
        },
        leg2NpvUsd: {
            validators: [Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.onlyNumber]
        },
        leg2BondPrice: {
            validators: [Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            validationMessages: [this.errorMessages.onlyNumber]
        },
        leg2DisCurve: {
            validators: [Validators.required],
            validationMessages: [this.errorMessages.required]
        },
        leg2IssueDate: {
            validators: [this.customValidatorsService.dateFormatValidator('D/M/YYYY')],
            validationMessages: [this.errorMessages.invalidDate]
        },
        leg2BondIssueDate: {
            validators: [this.customValidatorsService.dateFormatValidator('D/M/YYYY')],
            validationMessages: [this.errorMessages.invalidDate]
        },
        leg2PrincipalExchanged: {
            validators: [Validators.required],
            validationMessages: [this.errorMessages.required]
        }
    };

    instrumentConfigs = [{
        instrument: 'Cash',
        numberOfLegs: 1,
        numberOfColumn: 4,
        columns: {
            fund: { ...this.instrumentColumnConfig.fund, ['enable']: false },
            side: { ...this.instrumentColumnConfig.side, ['enable']: false },
            maturityDate: { ...this.instrumentColumnConfig.maturityDate, ['enable']: false },
            bondMaturityDate: { ...this.instrumentColumnConfig.bondMaturityDate, ['enable']: false },
            notional: { ...this.instrumentColumnConfig.notional, ['enable']: true, columnIndex: 1, columnName: 'amount'},
            payNotional: { ...this.instrumentColumnConfig.payNotional, ['enable']: false },
            irsNotional: { ...this.instrumentColumnConfig.notional, ['enable']: false },
            bondNotional: { ...this.instrumentColumnConfig.bondNotional, ['enable']: false },
            fxRate: { ...this.instrumentColumnConfig.fxRate, ['enable']: false },
            repoRate: { ...this.instrumentColumnConfig.repoRate, ['enable']: false },
            fixedRateCoupon: { ...this.instrumentColumnConfig.fixedRateCoupon, ['enable']: false },
            id: { ...this.instrumentColumnConfig.id, ['enable']: true, columnIndex: 0 , columnName: 'ID'},
            counterParty: { ...this.instrumentColumnConfig.counterParty, ['enable']: false },
            legType: { ...this.instrumentColumnConfig.legType, ['enable']: false },
            currency: { ...this.instrumentColumnConfig.currency, ['enable']: true, columnIndex: 2, columnName: 'Currency'},
            bondCurrency: { ...this.instrumentColumnConfig.bondCurrency, ['enable']: false },
            againstCurrency: { ...this.instrumentColumnConfig.againstCurrency, ['enable']: false },
            pnlAccounting: { ...this.instrumentColumnConfig.pnlAccounting, ['enable']: true, columnIndex: 3, columnName: 'Actual value'},
            haircutAfterPricing: { ...this.instrumentColumnConfig.haircutAfterPricing, ['enable']: false },
            priceAccounting: { ...this.instrumentColumnConfig.priceAccounting, ['enable']: false },
            couponFrequency: { ...this.instrumentColumnConfig.couponFrequency, ['enable']: false },
            spread: { ...this.instrumentColumnConfig.spread, ['enable']: false },
            refForward: { ...this.instrumentColumnConfig.refForward, ['enable']: false },
            npvUsd: { ...this.instrumentColumnConfig.npvUsd, ['enable']: false },
            bondPrice: { ...this.instrumentColumnConfig.bondPrice, ['enable']: false },
            disCurve: { ...this.instrumentColumnConfig.disCurve, ['enable']: false },
            issueDate: { ...this.instrumentColumnConfig.issueDate, ['enable']: false },
            bondIssueDate: { ...this.instrumentColumnConfig.bondIssueDate, ['enable']: false },
            principalExchanged: { ...this.instrumentColumnConfig.principalExchanged, ['enable']: false }
        },
        defaultSetter: (obj: InstrumentItem) => {
            obj.leg2Currency = obj.leg1Currency;
            obj.leg2IssueDate = moment('2015-01-01').format(this.dateFormats.YYYYMMDD_HHmmss);
            obj.leg2MaturityDate = moment('2100-01-01').format(this.dateFormats.YYYYMMDD_HHmmss);
        }
    }, {
        instrument: 'Fixed Bond',
        numberOfLegs: 1,
        numberOfColumn: 10,
        columns: {
            fund: { ...this.instrumentColumnConfig.fund, ['enable']: false },
            side: { ...this.instrumentColumnConfig.side, ['enable']: false },
            maturityDate: { ...this.instrumentColumnConfig.maturityDate, ['enable']: true, columnIndex: 2, columnName: 'Maturity'},
            bondMaturityDate: { ...this.instrumentColumnConfig.bondMaturityDate, ['enable']: false },
            notional: { ...this.instrumentColumnConfig.notional, ['enable']: true, columnIndex: 3, columnName: 'Notional'},
            payNotional: { ...this.instrumentColumnConfig.payNotional, ['enable']: false },
            irsNotional: { ...this.instrumentColumnConfig.notional, ['enable']: false },
            bondNotional: { ...this.instrumentColumnConfig.bondNotional, ['enable']: false },
            fxRate: { ...this.instrumentColumnConfig.fxRate, ['enable']: false },
            repoRate: { ...this.instrumentColumnConfig.repoRate, ['enable']: false },
            fixedRateCoupon: { ...this.instrumentColumnConfig.fixedRateCoupon, ['enable']: true, columnIndex: 5, columnName: 'Coupon(%)' },
            id: { ...this.instrumentColumnConfig.id, ['enable']: true, columnIndex: 0, columnName: 'ID'},
            counterParty: { ...this.instrumentColumnConfig.counterParty, ['enable']: false },
            legType: { ...this.instrumentColumnConfig.legType, ['enable']: false },
            currency: { ...this.instrumentColumnConfig.currency, ['enable']: true, columnIndex: 4, columnName: 'Currency' },
            bondCurrency: { ...this.instrumentColumnConfig.bondCurrency, ['enable']: false },
            againstCurrency: { ...this.instrumentColumnConfig.againstCurrency, ['enable']: false },
            pnlAccounting: { ...this.instrumentColumnConfig.pnlAccounting, ['enable']: false },
            haircutAfterPricing: { ...this.instrumentColumnConfig.haircutAfterPricing, ['enable']: true, columnIndex: 10, columnName: 'Haircut After Pricing' },
            priceAccounting: { ...this.instrumentColumnConfig.priceAccounting, ['enable']: false },
            couponFrequency: { ...this.instrumentColumnConfig.couponFrequency, ['enable']: true, columnIndex: 6, columnName: 'Frequency'},
            spread: { ...this.instrumentColumnConfig.spread, ['enable']: false },
            refForward: { ...this.instrumentColumnConfig.refForward, ['enable']: false },
            npvUsd: { ...this.instrumentColumnConfig.npvUsd, ['enable']: true, columnIndex: 8, columnName: 'NPV (USD)'},
            bondPrice: { ...this.instrumentColumnConfig.bondPrice, ['enable']: false },
            disCurve: { ...this.instrumentColumnConfig.disCurve, ['enable']: true, columnIndex: 7, columnName: 'DiscCurve'},
            issueDate: { ...this.instrumentColumnConfig.issueDate, ['enable']: true, columnIndex: 1, columnName: 'Issue Date' },
            bondIssueDate: { ...this.instrumentColumnConfig.bondIssueDate, ['enable']: false },
            principalExchanged: { ...this.instrumentColumnConfig.principalExchanged, ['enable']: false }
        },
        defaultSetter: (obj: InstrumentItem) => {
            obj.leg1PayBackNotional = 1;
            obj.leg2Currency = obj.leg1Currency;
            obj.leg2DisCurve = obj.leg1DisCurve;
            obj.leg2IssueDate = moment('2015-01-01').format(this.dateFormats.YYYYMMDD_HHmmss);
            obj.leg2MaturityDate = moment('2100-01-01').format(this.dateFormats.YYYYMMDD_HHmmss);
        }
    }, {
        instrument: 'Floater',
        numberOfLegs: 1,
        numberOfColumn: 10,
        columns: {
            fund: {...this.instrumentColumnConfig.fund, ['enable']: false},
            side: {...this.instrumentColumnConfig.side, ['enable']: false},
            maturityDate: {...this.instrumentColumnConfig.maturityDate, ['enable']: true, columnIndex: 2, columnName: 'Maturity'},
            bondMaturityDate: {...this.instrumentColumnConfig.bondMaturityDate, ['enable']: false},
            notional: {...this.instrumentColumnConfig.notional, ['enable']: true, columnIndex: 3, columnName: 'Notional'},
            payNotional: {...this.instrumentColumnConfig.payNotional, ['enable']: false},
            bondNotional: { ...this.instrumentColumnConfig.bondNotional, ['enable']: false },
            fxRate: { ...this.instrumentColumnConfig.fxRate, ['enable']: false },
            repoRate: { ...this.instrumentColumnConfig.repoRate, ['enable']: false },
            irsNotional: {...this.instrumentColumnConfig.notional, ['enable']: false},
            fixedRateCoupon: {...this.instrumentColumnConfig.fixedRateCoupon, ['enable']: false},
            id: {...this.instrumentColumnConfig.id, ['enable']: true, columnIndex: 0, columnName: 'ID'},
            counterParty: {...this.instrumentColumnConfig.counterParty, ['enable']: false},
            legType: {...this.instrumentColumnConfig.legType, ['enable']: false},
            currency: {...this.instrumentColumnConfig.currency, ['enable']: true, columnIndex: 4, columnName: 'Currency'},
            bondCurrency: { ...this.instrumentColumnConfig.bondCurrency, ['enable']: false},
            againstCurrency: { ...this.instrumentColumnConfig.againstCurrency, ['enable']: false },
            pnlAccounting: {...this.instrumentColumnConfig.pnlAccounting, ['enable']: false},
            haircutAfterPricing: { ...this.instrumentColumnConfig.haircutAfterPricing, ['enable']: true, columnIndex: 9, columnName: 'Haircut after Pricing'},
            priceAccounting: {...this.instrumentColumnConfig.priceAccounting, ['enable']: false},
            couponFrequency: {...this.instrumentColumnConfig.couponFrequency, ['enable']: true, columnIndex: 5, columnName: 'Frequency'},
            spread: {...this.instrumentColumnConfig.spread, ['enable']: false},
            refForward: {...this.instrumentColumnConfig.refForward, ['enable']: true, columnIndex: 6, columnName: 'Ref Forward'},
            npvUsd: {...this.instrumentColumnConfig.npvUsd, ['enable']: true, columnIndex: 8, columnName: 'NPV (USD)'},
            bondPrice: {...this.instrumentColumnConfig.bondPrice, ['enable']: false},
            disCurve: {...this.instrumentColumnConfig.disCurve, ['enable']: true, columnIndex: 7, columnName: 'DiscCurve'},
            issueDate: {...this.instrumentColumnConfig.issueDate, ['enable']: true, columnIndex: 1, columnName: 'Issue Date'},
            bondIssueDate: {...this.instrumentColumnConfig.bondIssueDate, ['enable']: false},
            principalExchanged: {...this.instrumentColumnConfig.principalExchanged, ['enable']: false}
        },
        defaultSetter: (obj: InstrumentItem) => {
            obj.leg1PayBackNotional = 1;
            obj.leg2Currency = obj.leg1Currency;
            obj.leg2DisCurve = obj.leg1DisCurve;
            obj.leg1LegType = '1';
            obj.leg2IssueDate = moment('2015-01-01').format(this.dateFormats.YYYYMMDD_HHmmss);
            obj.leg2MaturityDate = moment('2100-01-01').format(this.dateFormats.YYYYMMDD_HHmmss);
        }
    }, {
        instrument: 'IRS',
        numberOfLegs: 2,
        numberOfColumn: 13,
        columns: {
            fund: {...this.instrumentColumnConfig.fund, ['enable']: false, ['sameForBothLeg']: true},
            side: {...this.instrumentColumnConfig.side, ['enable']: false},
            maturityDate: {...this.instrumentColumnConfig.maturityDate, ['enable']: true, ['sameForBothLeg']: true, columnIndex: 2, columnName: 'Maturity'},
            bondMaturityDate: {...this.instrumentColumnConfig.bondMaturityDate, ['enable']: false},
            notional: {...this.instrumentColumnConfig.notional, ['enable']: false},
            payNotional: {...this.instrumentColumnConfig.payNotional, ['enable']: false},
            irsNotional: {...this.instrumentColumnConfig.notional, ['enable']: true, columnIndex: 3, columnName: 'Notional'},
            bondNotional: { ...this.instrumentColumnConfig.bondNotional, ['enable']: false },
            fxRate: { ...this.instrumentColumnConfig.fxRate, ['enable']: false },
            repoRate: { ...this.instrumentColumnConfig.repoRate, ['enable']: false },
            fixedRateCoupon: {...this.instrumentColumnConfig.fixedRateCoupon, ['enable']: true, columnIndex: 6, columnName: 'Coupon (%)'},
            id: {...this.instrumentColumnConfig.id, ['enable']: true, ['sameForBothLeg']: true, columnIndex: 0, columnName: 'ID'},
            counterParty: {...this.instrumentColumnConfig.counterParty, ['enable']: false, ['sameForBothLeg']: true},
            legType: {
                ...this.instrumentColumnConfig.irsLegType, ['enable']: true, columnIndex: 5, columnName: 'Leg Type',
                ['onLeg1Change']: this.instrumentFieldsOnChangeEvents.onIrsCmsLeg1LegTypeChange,
                ['onLeg2Change']: this.instrumentFieldsOnChangeEvents.onIrsCmsLeg2LegTypeChange
            },
            currency: {...this.instrumentColumnConfig.currency, ['enable']: true, columnIndex: 4, columnName: 'Currency'},
            bondCurrency: { ...this.instrumentColumnConfig.bondCurrency, ['enable']: false},
            againstCurrency: { ...this.instrumentColumnConfig.againstCurrency, ['enable']: false },
            pnlAccounting: {...this.instrumentColumnConfig.pnlAccounting, ['enable']: false},
            haircutAfterPricing: {...this.instrumentColumnConfig.haircutAfterPricing, ['enable']: false, columnName: 'Haircut After Pricing'},
            priceAccounting: {...this.instrumentColumnConfig.priceAccounting, ['enable']: false, ['sameForBothLeg']: true},
            couponFrequency: {...this.instrumentColumnConfig.couponFrequency, ['enable']: true, columnIndex: 9, columnName: 'Frequency'},
            spread: {...this.instrumentColumnConfig.spread, ['enable']: true, columnIndex: 7, columnName: 'Spread'},
            refForward: {...this.instrumentColumnConfig.refForward, ['enable']: true, columnIndex: 8, columnName: 'Ref Forward'},
            npvUsd: {...this.instrumentColumnConfig.npvUsd, ['enable']: true, columnIndex: 11, columnName: 'NPV (USD)'},
            bondPrice: {...this.instrumentColumnConfig.bondPrice, ['enable']: false},
            disCurve: {...this.instrumentColumnConfig.disCurve, ['enable']: true, columnIndex: 10, columnName: 'DiscCurve'},
            issueDate: {...this.instrumentColumnConfig.issueDate, ['enable']: true, ['sameForBothLeg']: true, columnIndex: 1, columnName: 'Issue Date'},
            bondIssueDate: {...this.instrumentColumnConfig.bondIssueDate, ['enable']: false},
            principalExchanged: {...this.instrumentColumnConfig.principalExchanged, ['enable']: true, columnIndex: 12, columnName: 'Principal Exchanged'}
        },
        defaultSetter: (obj: InstrumentItem) => {
            obj.leg2Currency = obj.leg1Currency;
        }
    }, {
        instrument: 'CCS',
        numberOfLegs: 2,
        numberOfColumn: 13,
        columns: {
            fund: {...this.instrumentColumnConfig.fund, ['enable']: false, ['sameForBothLeg']: true},
            side: {...this.instrumentColumnConfig.side, ['enable']: false},
            maturityDate: {...this.instrumentColumnConfig.maturityDate, ['enable']: true, ['sameForBothLeg']: true, columnIndex: 2, columnName: 'Maturity'},
            bondMaturityDate: {...this.instrumentColumnConfig.bondMaturityDate, ['enable']: false},
            notional: {...this.instrumentColumnConfig.notional, ['enable']: false},
            payNotional: {...this.instrumentColumnConfig.payNotional, ['enable']: false},
            irsNotional: {...this.instrumentColumnConfig.notional, ['enable']: true, columnIndex: 3, columnName: 'Notional'},
            bondNotional: { ...this.instrumentColumnConfig.bondNotional, ['enable']: false },
            fxRate: { ...this.instrumentColumnConfig.fxRate, ['enable']: false },
            repoRate: { ...this.instrumentColumnConfig.repoRate, ['enable']: false },
            fixedRateCoupon: {...this.instrumentColumnConfig.fixedRateCoupon, ['enable']: true, columnIndex: 6, columnName: 'Coupon (%)'},
            id: {...this.instrumentColumnConfig.id, ['enable']: true, ['sameForBothLeg']: true, columnIndex: 0, columnName: 'ID'},
            counterParty: {...this.instrumentColumnConfig.counterParty, ['enable']: false, ['sameForBothLeg']: true},
            legType: {
                ...this.instrumentColumnConfig.irsLegType, ['enable']: true, columnIndex: 5, columnName: 'Leg Type',
                //['onLeg1Change']: this.instrumentFieldsOnChangeEvents.onIrsCmsLeg1LegTypeChange,
                //['onLeg2Change']: this.instrumentFieldsOnChangeEvents.onIrsCmsLeg2LegTypeChange
            },
            currency: {...this.instrumentColumnConfig.currency, ['enable']: true, columnIndex: 4, columnName: 'Currency'},
            bondCurrency: { ...this.instrumentColumnConfig.bondCurrency, ['enable']: false},
            againstCurrency: { ...this.instrumentColumnConfig.againstCurrency, ['enable']: false },
            pnlAccounting: {...this.instrumentColumnConfig.pnlAccounting, ['enable']: false},
            haircutAfterPricing: {...this.instrumentColumnConfig.haircutAfterPricing, ['enable']: false, columnName: 'Haircut After Pricing'},
            priceAccounting: {...this.instrumentColumnConfig.priceAccounting, ['enable']: false, ['sameForBothLeg']: true},
            couponFrequency: {...this.instrumentColumnConfig.couponFrequency, ['enable']: true, columnIndex: 9, columnName: 'Frequency'},
            spread: {...this.instrumentColumnConfig.spread, ['enable']: true, columnIndex: 7, columnName: 'Spread'},
            refForward: {...this.instrumentColumnConfig.refForward, ['enable']: true, columnIndex: 8, columnName: 'Ref Forward'},
            npvUsd: {...this.instrumentColumnConfig.npvUsd, ['enable']: true, columnIndex: 11, columnName: 'NPV (USD)'},
            bondPrice: {...this.instrumentColumnConfig.bondPrice, ['enable']: false},
            disCurve: {...this.instrumentColumnConfig.disCurve, ['enable']: true, columnIndex: 10, columnName: 'DiscCurve'},
            issueDate: {...this.instrumentColumnConfig.issueDate, ['enable']: true, ['sameForBothLeg']: true, columnIndex: 1, columnName: 'Issue Date'},
            bondIssueDate: {...this.instrumentColumnConfig.bondIssueDate, ['enable']: false},
            principalExchanged: {...this.instrumentColumnConfig.principalExchanged, ['enable']: true, columnIndex: 12, columnName: 'Principal Exchanged'}
        },
        /*defaultSetter: (obj: InstrumentItem) => {
            obj.leg2Currency = obj.leg1Currency;
        }*/
    }, {
        instrument: 'CMS',
        numberOfLegs: 2,
        numberOfColumn: 13,
        columns: {
            fund: {...this.instrumentColumnConfig.fund, ['enable']: true, ['sameForBothLeg']: true},
            side: {...this.instrumentColumnConfig.side, ['enable']: false},
            maturityDate: {...this.instrumentColumnConfig.maturityDate, ['enable']: true, ['sameForBothLeg']: true},
            bondMaturityDate: {...this.instrumentColumnConfig.bondMaturityDate, ['enable']: false},
            notional: {...this.instrumentColumnConfig.notional, ['enable']: true},
            payNotional: {...this.instrumentColumnConfig.payNotional, ['enable']: false},
            irsNotional: {...this.instrumentColumnConfig.notional, ['enable']: false},
            bondNotional: { ...this.instrumentColumnConfig.bondNotional, ['enable']: false },
            fxRate: { ...this.instrumentColumnConfig.fxRate, ['enable']: false },
            repoRate: { ...this.instrumentColumnConfig.repoRate, ['enable']: false },
            fixedRateCoupon: {...this.instrumentColumnConfig.fixedRateCoupon, ['enable']: true},
            id: {...this.instrumentColumnConfig.id, ['enable']: true, ['sameForBothLeg']: true},
            counterParty: {...this.instrumentColumnConfig.counterParty, ['enable']: true, ['sameForBothLeg']: true},
            legType: {...this.instrumentColumnConfig.legType, ['enable']: true,
                //['onLeg1Change']: this.instrumentFieldsOnChangeEvents.onIrsCmsLeg1LegTypeChange,
                //['onLeg2Change']: this.instrumentFieldsOnChangeEvents.onIrsCmsLeg2LegTypeChange
            },
            currency: {...this.instrumentColumnConfig.currency, ['enable']: true},
            bondCurrency: { ...this.instrumentColumnConfig.bondCurrency, ['enable']: false},
            againstCurrency: { ...this.instrumentColumnConfig.againstCurrency, ['enable']: false },
            pnlAccounting: {...this.instrumentColumnConfig.pnlAccounting, ['enable']: true},
            haircutAfterPricing: {...this.instrumentColumnConfig.haircutAfterPricing, ['enable']: false},
            priceAccounting: {...this.instrumentColumnConfig.priceAccounting, ['enable']: true, ['sameForBothLeg']: true},
            couponFrequency: {...this.instrumentColumnConfig.couponFrequency, ['enable']: true},
            spread: {...this.instrumentColumnConfig.spread, ['enable']: true},
            refForward: {...this.instrumentColumnConfig.refForward, ['enable']: true},
            npvUsd: {...this.instrumentColumnConfig.npvUsd, ['enable']: false},
            bondPrice: {...this.instrumentColumnConfig.bondPrice, ['enable']: false},
            disCurve: {...this.instrumentColumnConfig.disCurve, ['enable']: false},
            issueDate: {...this.instrumentColumnConfig.issueDate, ['enable']: false},
            bondIssueDate: {...this.instrumentColumnConfig.bondIssueDate, ['enable']: false},
            principalExchanged: {...this.instrumentColumnConfig.principalExchanged, ['enable']: true}
        }
    }, {
        instrument: 'FXFwd',
        numberOfLegs: 1,
        numberOfColumn: 7,
        columns: {
            fund: {...this.instrumentColumnConfig.fund, ['enable']: false, ['sameForBothLeg']: true},
            side: {...this.instrumentColumnConfig.side, ['enable']: false},
            maturityDate: {...this.instrumentColumnConfig.maturityDate, ['enable']: true, columnIndex: 2, columnName: 'Delivery Date'},
            bondMaturityDate: {...this.instrumentColumnConfig.bondMaturityDate, ['enable']: false},
            notional: {...this.instrumentColumnConfig.notional, ['enable']: true, columnIndex: 3, columnName: 'Notional'},
            payNotional: {...this.instrumentColumnConfig.payNotional, ['enable']: false },
            irsNotional: {...this.instrumentColumnConfig.notional, ['enable']: false},
            bondNotional: { ...this.instrumentColumnConfig.bondNotional, ['enable']: false },
            fxRate: { ...this.instrumentColumnConfig.fxRate, ['enable']: true, columnIndex: 4, columnName: 'FX Rate'},
            repoRate: { ...this.instrumentColumnConfig.repoRate, ['enable']: false },
            fixedRateCoupon: {...this.instrumentColumnConfig.fixedRateCoupon, ['enable']: false},
            id: {...this.instrumentColumnConfig.id, ['enable']: true, ['sameForBothLeg']: true, columnIndex: 0, columnName: 'ID'},
            counterParty: {...this.instrumentColumnConfig.counterParty, ['enable']: false},
            legType: {...this.instrumentColumnConfig.legType, ['enable']: false},
            currency: {...this.instrumentColumnConfig.currency, ['enable']: true, columnIndex: 5, columnName: 'Currency'},
            bondCurrency: { ...this.instrumentColumnConfig.bondCurrency, ['enable']: false },
            againstCurrency: { ...this.instrumentColumnConfig.againstCurrency, ['enable']: true, columnIndex: 6, columnName: 'Against Currency'},
            pnlAccounting: {...this.instrumentColumnConfig.pnlAccounting, ['enable']: false},
            haircutAfterPricing: {...this.instrumentColumnConfig.haircutAfterPricing, ['enable']: false},
            priceAccounting: {...this.instrumentColumnConfig.priceAccounting, ['enable']: false},
            couponFrequency: {...this.instrumentColumnConfig.couponFrequency, ['enable']: false},
            spread: {...this.instrumentColumnConfig.spread, ['enable']: false},
            refForward: {...this.instrumentColumnConfig.refForward, ['enable']: false},
            npvUsd: {...this.instrumentColumnConfig.npvUsd, ['enable']: false},
            bondPrice: {...this.instrumentColumnConfig.bondPrice, ['enable']: false},
            disCurve: {...this.instrumentColumnConfig.disCurve, ['enable']: false},
            issueDate: {...this.instrumentColumnConfig.issueDate, ['enable']: true, columnIndex: 1, issueDate: 'Issue Date'},
            bondIssueDate: {...this.instrumentColumnConfig.bondIssueDate, ['enable']: false},
            principalExchanged: {...this.instrumentColumnConfig.principalExchanged, ['enable']: false}
        }
    }, {
        instrument: 'FXSwp',
        numberOfLegs: 1,
        numberOfColumn: 7,
        columns: {
            fund: {...this.instrumentColumnConfig.fund, ['enable']: false, ['sameForBothLeg']: true},
            side: {...this.instrumentColumnConfig.side, ['enable']: false},
            maturityDate: {...this.instrumentColumnConfig.maturityDate, ['enable']: true, ['sameForBothLeg']: true, columnIndex: 2, columnName: 'Delivery Date'},
            bondMaturityDate: {...this.instrumentColumnConfig.bondMaturityDate, ['enable']: false},
            notional: {...this.instrumentColumnConfig.notional, ['enable']: true, columnIndex: 3, columnName: 'Notional'},
            payNotional: {...this.instrumentColumnConfig.payNotional, ['enable']: false },
            irsNotional: {...this.instrumentColumnConfig.notional, ['enable']: false},
            bondNotional: { ...this.instrumentColumnConfig.bondNotional, ['enable']: false },
            fxRate: { ...this.instrumentColumnConfig.fxRate, ['enable']: true, columnIndex: 4, columnName: 'FX Rate'},
            repoRate: { ...this.instrumentColumnConfig.repoRate, ['enable']: false },
            fixedRateCoupon: {...this.instrumentColumnConfig.fixedRateCoupon, ['enable']: false},
            id: {...this.instrumentColumnConfig.id, ['enable']: true, ['sameForBothLeg']: true, columnIndex: 0, columnName: 'ID'},
            counterParty: {...this.instrumentColumnConfig.counterParty, ['enable']: false, ['sameForBothLeg']: true},
            legType: {...this.instrumentColumnConfig.legType, ['enable']: false},
            currency: {...this.instrumentColumnConfig.currency, ['enable']: true, columnIndex: 5, columnName: 'Currency'},
            bondCurrency: { ...this.instrumentColumnConfig.bondCurrency, ['enable']: false},
            againstCurrency: { ...this.instrumentColumnConfig.againstCurrency, ['enable']: true, columnIndex: 6, columnName: 'Against Currency'},
            pnlAccounting: {...this.instrumentColumnConfig.pnlAccounting, ['enable']: false},
            haircutAfterPricing: {...this.instrumentColumnConfig.haircutAfterPricing, ['enable']: false},
            priceAccounting: {...this.instrumentColumnConfig.priceAccounting, ['enable']: false},
            couponFrequency: {...this.instrumentColumnConfig.couponFrequency, ['enable']: false},
            spread: {...this.instrumentColumnConfig.spread, ['enable']: false},
            refForward: {...this.instrumentColumnConfig.refForward, ['enable']: false},
            npvUsd: {...this.instrumentColumnConfig.npvUsd, ['enable']: false},
            bondPrice: {...this.instrumentColumnConfig.bondPrice, ['enable']: false},
            disCurve: {...this.instrumentColumnConfig.disCurve, ['enable']: false},
            issueDate: {...this.instrumentColumnConfig.issueDate, ['enable']: true, columnIndex: 1, columnName: 'Issue Date'},
            bondIssueDate: {...this.instrumentColumnConfig.bondIssueDate, ['enable']: false},
            principalExchanged: {...this.instrumentColumnConfig.principalExchanged, ['enable']: false}
        }
    }, {
        instrument: 'Repo',
        numberOfLegs: 1,
        numberOfColumn: 13,
        columns: {
            fund: {...this.instrumentColumnConfig.fund, ['enable']: false, ['sameForBothLeg']: true},
            side: {...this.instrumentColumnConfig.side, ['enable']: false},
            maturityDate: {...this.instrumentColumnConfig.maturityDate, ['enable']: true, columnIndex: 2, columnName: 'Maturity', class: 'px-w-65'},
            bondMaturityDate: {...this.instrumentColumnConfig.bondMaturityDate, ['enable']: true, columnIndex: 7, columnName: 'Bond Maturity'},
            notional: {...this.instrumentColumnConfig.notional, ['enable']: true, columnIndex: 3, columnName: 'Repo Notional', class: 'px-w-85'},
            payNotional: { ...this.instrumentColumnConfig.payNotional, ['enable']: false },
            irsNotional: {...this.instrumentColumnConfig.notional, ['enable']: false},
            bondNotional: { ...this.instrumentColumnConfig.bondNotional, ['enable']: true, columnIndex: 8, columnName: 'Bond Notional'},
            fxRate: { ...this.instrumentColumnConfig.fxRate, ['enable']: false },
            repoRate: { ...this.instrumentColumnConfig.repoRate, ['enable']: true, columnIndex: 4, columnName: 'Repo Rate', class: 'px-w-65'},
            fixedRateCoupon: {...this.instrumentColumnConfig.fixedRateCoupon, ['enable']: true, columnIndex: 10, columnName: 'Coupon (%)'},
            id: {...this.instrumentColumnConfig.id, ['enable']: true, ['sameForBothLeg']: true, columnIndex: 0, columnName: 'ID'},
            counterParty: {...this.instrumentColumnConfig.counterParty, ['enable']: false, ['sameForBothLeg']: true},
            legType: {...this.instrumentColumnConfig.legType, ['enable']: false},
            currency: {...this.instrumentColumnConfig.currency, ['enable']: true, columnIndex: 5, columnName: 'Repo Currency', class: 'px-w-85'},
            bondCurrency: { ...this.instrumentColumnConfig.bondCurrency, ['enable']: true, columnIndex: 9, columnName: 'Bond Currency'},
            againstCurrency: { ...this.instrumentColumnConfig.againstCurrency, ['enable']: false },
            pnlAccounting: {...this.instrumentColumnConfig.pnlAccounting, ['enable']: false},
            haircutAfterPricing: {...this.instrumentColumnConfig.haircutAfterPricing, ['enable']: false},
            priceAccounting: {...this.instrumentColumnConfig.priceAccounting, ['enable']: false},
            couponFrequency: {...this.instrumentColumnConfig.couponFrequency, ['enable']: true, columnIndex: 11, columnName: 'Coupon Frequency', class: 'px-w-115'},
            spread: {...this.instrumentColumnConfig.spread, ['enable']: false},
            refForward: {...this.instrumentColumnConfig.refForward, ['enable']: false},
            npvUsd: {...this.instrumentColumnConfig.npvUsd, ['enable']: false},
            bondPrice: {...this.instrumentColumnConfig.bondPrice, ['enable']: false },
            disCurve: {...this.instrumentColumnConfig.disCurve, ['enable']: true, columnIndex: 12, columnName: 'Bond DiscCurve'},
            issueDate: {...this.instrumentColumnConfig.issueDate, ['enable']: true, columnIndex: 1, columnName: 'Issue Date', class: 'px-w-75'},
            bondIssueDate: {...this.instrumentColumnConfig.bondIssueDate, ['enable']: true, columnIndex: 6, columnName: 'Bond Issue Date'},
            principalExchanged: {...this.instrumentColumnConfig.principalExchanged, ['enable']: false}
        }
    }, {
        instrument: 'Equity Option',
        numberOfLegs: 1,
        numberOfColumn: 8,
        columns: {
            fund: {...this.instrumentColumnConfig.fund, ['enable']: true},
            side: {...this.instrumentColumnConfig.side, ['enable']: false},
            maturityDate: {...this.instrumentColumnConfig.maturityDate, ['enable']: true},
            bondMaturityDate: {...this.instrumentColumnConfig.bondMaturityDate, ['enable']: false},
            notional: {...this.instrumentColumnConfig.notional, ['enable']: true},
            payNotional: {...this.instrumentColumnConfig.payNotional, ['enable']: false},
            bondNotional: { ...this.instrumentColumnConfig.bondNotional, ['enable']: false },
            fxRate: { ...this.instrumentColumnConfig.fxRate, ['enable']: false },
            repoRate: { ...this.instrumentColumnConfig.repoRate, ['enable']: false },
            irsNotional: {...this.instrumentColumnConfig.notional, ['enable']: false},
            fixedRateCoupon: {...this.instrumentColumnConfig.fixedRateCoupon, ['enable']: false},
            id: {...this.instrumentColumnConfig.id, ['enable']: true},
            counterParty: {...this.instrumentColumnConfig.counterParty, ['enable']: true},
            legType: {...this.instrumentColumnConfig.legType, ['enable']: false},
            currency: {...this.instrumentColumnConfig.currency, ['enable']: true},
            bondCurrency: { ...this.instrumentColumnConfig.bondCurrency, ['enable']: false},
            againstCurrency: { ...this.instrumentColumnConfig.againstCurrency, ['enable']: false },
            pnlAccounting: {...this.instrumentColumnConfig.pnlAccounting, ['enable']: true},
            haircutAfterPricing: {...this.instrumentColumnConfig.haircutAfterPricing, ['enable']: false},
            priceAccounting: {...this.instrumentColumnConfig.priceAccounting, ['enable']: true},
            couponFrequency: {...this.instrumentColumnConfig.couponFrequency, ['enable']: false},
            spread: {...this.instrumentColumnConfig.spread, ['enable']: false},
            refForward: {...this.instrumentColumnConfig.refForward, ['enable']: false},
            npvUsd: {...this.instrumentColumnConfig.npvUsd, ['enable']: false},
            bondPrice: {...this.instrumentColumnConfig.bondPrice, ['enable']: false},
            disCurve: {...this.instrumentColumnConfig.disCurve, ['enable']: false},
            issueDate: {...this.instrumentColumnConfig.issueDate, ['enable']: false},
            bondIssueDate: {...this.instrumentColumnConfig.bondIssueDate, ['enable']: false},
            principalExchanged: {...this.instrumentColumnConfig.principalExchanged, ['enable']: false}
        }
    }, {
        instrument: 'FX Option',
        numberOfLegs: 1,
        numberOfColumn: 9,
        columns: {
            fund: {...this.instrumentColumnConfig.fund, ['enable']: true},
            side: {...this.instrumentColumnConfig.side, ['enable']: false},
            maturityDate: {...this.instrumentColumnConfig.maturityDate, ['enable']: true},
            bondMaturityDate: {...this.instrumentColumnConfig.bondMaturityDate, ['enable']: false},
            notional: {...this.instrumentColumnConfig.notional, ['enable']: true},
            payNotional: {...this.instrumentColumnConfig.payNotional, ['enable']: false},
            irsNotional: {...this.instrumentColumnConfig.notional, ['enable']: false},
            bondNotional: { ...this.instrumentColumnConfig.bondNotional, ['enable']: false },
            fxRate: { ...this.instrumentColumnConfig.fxRate, ['enable']: false },
            repoRate: { ...this.instrumentColumnConfig.repoRate, ['enable']: false },
            fixedRateCoupon: {...this.instrumentColumnConfig.fixedRateCoupon, ['enable']: false},
            id: {...this.instrumentColumnConfig.id, ['enable']: true},
            counterParty: {...this.instrumentColumnConfig.counterParty, ['enable']: true},
            legType: {...this.instrumentColumnConfig.legType, ['enable']: false},
            currency: {...this.instrumentColumnConfig.currency, ['enable']: true},
            bondCurrency: { ...this.instrumentColumnConfig.bondCurrency, ['enable']: false},
            againstCurrency: { ...this.instrumentColumnConfig.againstCurrency, ['enable']: false },
            pnlAccounting: {...this.instrumentColumnConfig.pnlAccounting, ['enable']: true},
            haircutAfterPricing: {...this.instrumentColumnConfig.haircutAfterPricing, ['enable']: false},
            priceAccounting: {...this.instrumentColumnConfig.priceAccounting, ['enable']: true},
            couponFrequency: {...this.instrumentColumnConfig.couponFrequency, ['enable']: false},
            spread: {...this.instrumentColumnConfig.spread, ['enable']: false},
            refForward: {...this.instrumentColumnConfig.refForward, ['enable']: false},
            npvUsd: {...this.instrumentColumnConfig.npvUsd, ['enable']: false},
            bondPrice: {...this.instrumentColumnConfig.bondPrice, ['enable']: false},
            disCurve: {...this.instrumentColumnConfig.disCurve, ['enable']: false},
            issueDate: {...this.instrumentColumnConfig.issueDate, ['enable']: false},
            bondIssueDate: {...this.instrumentColumnConfig.bondIssueDate, ['enable']: false},
            principalExchanged: {...this.instrumentColumnConfig.principalExchanged, ['enable']: false}
        }
    }, {
        instrument: 'Bond Future',
        numberOfLegs: 1,
        numberOfColumn: 11,
        columns: {
            fund: {...this.instrumentColumnConfig.fund, ['enable']: false, ['sameForBothLeg']: true},
            side: {...this.instrumentColumnConfig.side, ['enable']: false},
            maturityDate: {...this.instrumentColumnConfig.maturityDate, ['enable']: true, columnIndex: 2, columnName: 'Maturity'},
            bondMaturityDate: {...this.instrumentColumnConfig.bondMaturityDate, ['enable']: true, columnIndex: 9, columnName: 'Bond maturity'},
            notional: {...this.instrumentColumnConfig.notional, ['enable']: true, columnIndex: 3, columnName: 'Notional'},
            payNotional: {...this.instrumentColumnConfig.payNotional, ['enable']: false},
            irsNotional: {...this.instrumentColumnConfig.notional, ['enable']: false},
            bondNotional: { ...this.instrumentColumnConfig.bondNotional, ['enable']: false },
            fxRate: { ...this.instrumentColumnConfig.fxRate, ['enable']: false },
            repoRate: { ...this.instrumentColumnConfig.repoRate, ['enable']: false },
            fixedRateCoupon: {...this.instrumentColumnConfig.fixedRateCoupon, ['enable']: true, columnIndex: 7, columnName: 'Coupon (%)'},
            id: {...this.instrumentColumnConfig.id, ['enable']: true, ['sameForBothLeg']: true, columnIndex: 0, columnName: 'ID'},
            counterParty: {...this.instrumentColumnConfig.counterParty, ['enable']: false, ['sameForBothLeg']: true},
            legType: {...this.instrumentColumnConfig.legType, ['enable']: false},
            currency: {...this.instrumentColumnConfig.currency, ['enable']: true, columnIndex: 4, columnName: 'Currency'},
            bondCurrency: { ...this.instrumentColumnConfig.bondCurrency, ['enable']: true, columnIndex: 6, columnName: 'Bond Currency'},
            againstCurrency: { ...this.instrumentColumnConfig.againstCurrency, ['enable']: false },
            pnlAccounting: {...this.instrumentColumnConfig.pnlAccounting, ['enable']: false},
            haircutAfterPricing: {...this.instrumentColumnConfig.haircutAfterPricing, ['enable']: false},
            priceAccounting: {...this.instrumentColumnConfig.priceAccounting, ['enable']: false, ['sameForBothLeg']: true},
            couponFrequency: {...this.instrumentColumnConfig.couponFrequency, ['enable']: true, columnIndex: 8, columnName: 'Frequency'},
            spread: {...this.instrumentColumnConfig.spread, ['enable']: false},
            refForward: {...this.instrumentColumnConfig.refForward, ['enable']: false},
            npvUsd: {...this.instrumentColumnConfig.npvUsd, ['enable']: true, columnIndex: 5, columnName: 'NPV (USD)'},
            bondPrice: { ...this.instrumentColumnConfig.bondPrice, ['enable']: true, columnIndex: 10, columnName: 'Bond Price'},
            disCurve: {...this.instrumentColumnConfig.disCurve, ['enable']: false},
            issueDate: {...this.instrumentColumnConfig.issueDate, ['enable']: true, columnIndex: 1, columnName: 'Issue Date'},
            bondIssueDate: {...this.instrumentColumnConfig.bondIssueDate, ['enable']: false},
            principalExchanged: {...this.instrumentColumnConfig.principalExchanged, ['enable']: false}
        }
    }, {
        instrument: 'Bond Forward',
        numberOfLegs: 1,
        numberOfColumn: 11,
        columns: {
            fund: {...this.instrumentColumnConfig.fund, ['enable']: false, ['sameForBothLeg']: true},
            side: {...this.instrumentColumnConfig.side, ['enable']: false},
            maturityDate: {...this.instrumentColumnConfig.maturityDate, ['enable']: true, columnIndex: 2, columnName: 'Maturity'},
            bondMaturityDate: {...this.instrumentColumnConfig.bondMaturityDate, ['enable']: true, columnIndex: 9, columnName: 'Bond maturity'},
            notional: {...this.instrumentColumnConfig.notional, ['enable']: true, columnIndex: 3, columnName: 'Notional'},
            payNotional: {...this.instrumentColumnConfig.payNotional, ['enable']: false},
            irsNotional: {...this.instrumentColumnConfig.notional, ['enable']: false},
            bondNotional: { ...this.instrumentColumnConfig.bondNotional, ['enable']: false },
            fxRate: { ...this.instrumentColumnConfig.fxRate, ['enable']: false },
            repoRate: { ...this.instrumentColumnConfig.repoRate, ['enable']: false },
            fixedRateCoupon: {...this.instrumentColumnConfig.fixedRateCoupon, ['enable']: true, columnIndex: 7, columnName: 'Coupon (%)'},
            id: {...this.instrumentColumnConfig.id, ['enable']: true, ['sameForBothLeg']: true, columnIndex: 0, columnName: 'ID'},
            counterParty: {...this.instrumentColumnConfig.counterParty, ['enable']: false, ['sameForBothLeg']: true},
            legType: {...this.instrumentColumnConfig.legType, ['enable']: false},
            currency: {...this.instrumentColumnConfig.currency, ['enable']: true, columnIndex: 4, columnName: 'Currency'},
            bondCurrency: { ...this.instrumentColumnConfig.bondCurrency, ['enable']: true, columnIndex: 6, columnName: 'Bond Currency'},
            againstCurrency: { ...this.instrumentColumnConfig.againstCurrency, ['enable']: false },
            pnlAccounting: {...this.instrumentColumnConfig.pnlAccounting, ['enable']: false},
            haircutAfterPricing: {...this.instrumentColumnConfig.haircutAfterPricing, ['enable']: false},
            priceAccounting: {...this.instrumentColumnConfig.priceAccounting, ['enable']: false, ['sameForBothLeg']: true},
            couponFrequency: {...this.instrumentColumnConfig.couponFrequency, ['enable']: true, columnIndex: 8, columnName: 'Frequency'},
            spread: {...this.instrumentColumnConfig.spread, ['enable']: false},
            refForward: {...this.instrumentColumnConfig.refForward, ['enable']: false},
            npvUsd: {...this.instrumentColumnConfig.npvUsd, ['enable']: true, columnIndex: 5, columnName: 'NPV (USD)'},
            bondPrice: { ...this.instrumentColumnConfig.bondPrice, ['enable']: true, columnIndex: 10, columnName: 'Bond Price'},
            disCurve: {...this.instrumentColumnConfig.disCurve, ['enable']: false},
            issueDate: {...this.instrumentColumnConfig.issueDate, ['enable']: true, columnIndex: 1, columnName: 'Issue Date'},
            bondIssueDate: {...this.instrumentColumnConfig.bondIssueDate, ['enable']: false},
            principalExchanged: {...this.instrumentColumnConfig.principalExchanged, ['enable']: false}
        }
    }];

    modelPopupMode = {
        create: 'create',
        tradeExecuted: 'trade executed',
        edit: 'edit',
        view: 'view'
    };

    notificationPopupMode = {
        deleteInstrument: {
            name: 'delete instrument',
            title: 'DELETE INSTRUMENT',
            text: 'This instrument will be deleted.',
            question: 'Are you sure you want to delete it?',
            continueButton: 'DELETE INSTRUMENT'
        },
        deleteTrade: {
            name: 'delete trade',
            title: 'DELETE TRADE',
            text: 'This trade and its instruments will be permanently deleted.',
            question: 'Are you sure you want to delete them?',
            continueButton: 'DELETE TRADE'
        },
        deleteModel: {
            name: 'delete model',
            title: 'DELETE MODEL',
            text: 'This model and its instruments will be permanently deleted.',
            question: 'Are you sure you want to delete them?',
            continueButton: 'DELETE MODEL'
        },
        executeTrade: {
            name: 'executeTrade',
            title: 'CONFIRM AND EXECUTED TRADE',
            text: 'The modelled trade and its instruments will be added to the application\'s list of executed instruments',
            question: 'Are you sure you want to continue?',
            continueButton: 'ADD TRADE(S)'
        },
        modelSimulationRequest: {
            name: 'modelSimulationRequest',
            title: 'CONFIRM',
            text: 'Pricing the selected model may take some time. A request will be added to the pricing queue.',
            question: 'Are you sure you want to continue?',
            continueButton: 'YES'
        }
    };

    dateFormats = {
        YYYYMMDD_HHmmss: 'YYYY-MM-DD HH:mm:ss',
        DMYYYY: 'D/M/YYYY'
    };

    modelStatus = {
        pending: 'Pending',
        inProgress: 'In Progress',
        completed: 'Completed',
        expired: 'Expired'
    };

    timesteps = [0, 0.5, 1, 2, 3, 4, 5, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36, 39, 42, 45, 48, 51, 54, 57, 60, 63, 66,
        69, 72, 75, 78, 81, 84, 87, 90, 93, 96, 99, 102, 105, 108, 111, 114, 117, 120];

    constructor(private customValidatorsService: CustomValidatorsService) {
    }
}
